/* eslint-disable no-undef */
import { computed } from 'vue';
import { useMap } from '~/common/composables/mapbox/maps';
import { load_js_css_file, removeLoadedFile } from '~/common/utils/load-script.util';
import { useTerraStore } from '~/terra/store/terra.store';

export function useBarcodeScanHelper() {
  const STATUS_COLORS = {
    pending: '#F04438',
    inprogress: '#FEC84B',
    completed: '#12B76A',
    selected: '#1570EF',
  };

  const STATUS_OUTLINE_COLORS = {
    pending: '#D92D20',
    inprogress: '#FDB022',
    completed: '#039855',
    selected: '#1570EF',
  };

  const STATUS_LABELS = {
    pending: 'Pending',
    inprogress: 'In progress',
    completed: 'Complete',
  };

  const getFeatureStyle = computed(() => ({
    default: {
      // Fill style
      'fill-color': [
        'case',
        ['==', ['get', 'barcode_scan_selected'], true],
        STATUS_COLORS.selected,
        ['==', ['get', 'barcode_scan_status'], 'pending'],
        STATUS_COLORS.pending,
        ['==', ['get', 'barcode_scan_status'], 'inprogress'],
        STATUS_COLORS.inprogress,
        ['==', ['get', 'barcode_scan_status'], 'completed'],
        STATUS_COLORS.completed,
        STATUS_COLORS.pending,
      ],
      'fill-opacity': 1,

      // Outline style for fill layers
      'fill-outline-color': [
        'case',
        ['==', ['get', 'barcode_scan_selected'], true],
        STATUS_COLORS.selected,
        ['==', ['get', 'barcode_scan_status'], 'pending'],
        STATUS_COLORS.pending,
        ['==', ['get', 'barcode_scan_status'], 'inprogress'],
        STATUS_COLORS.inprogress,
        ['==', ['get', 'barcode_scan_status'], 'completed'],
        STATUS_COLORS.completed,
        STATUS_COLORS.pending,
      ],

      // Line style (for both line geometries and fill outlines)
      'line-color': [
        'case',
        ['==', ['get', 'barcode_scan_selected'], true],
        STATUS_OUTLINE_COLORS.selected,
        ['==', ['get', 'barcode_scan_status'], 'pending'],
        STATUS_OUTLINE_COLORS.pending,
        ['==', ['get', 'barcode_scan_status'], 'inprogress'],
        STATUS_OUTLINE_COLORS.inprogress,
        ['==', ['get', 'barcode_scan_status'], 'completed'],
        STATUS_OUTLINE_COLORS.completed,
        STATUS_OUTLINE_COLORS.pending,
      ],
      'line-width': 2.5,
      'line-opacity': 1,
    },
  }));

  function loadBarcodeScanImages(map) {
    const { loadImages } = useMap();

    loadImages(map, '/img/icons/scan-duplicates.png', 'scan-duplicates');
  }

  async function addBarcodeScanSymbols() {
    try {
      const turf = await import('@turf/turf');
      const terra_store = useTerraStore();

      const filtered_features = terra_store.barcode_scan_features.filter(
        feature => feature.properties.barcode_scan_duplicates > 0,
      );

      const symbol_collection = filtered_features.map((feature) => {
        let center;

        if (feature.geometry.type === 'Point') {
          center = feature.geometry.coordinates;
        }
        else {
          const centroid = turf.centroid(feature);
          center = centroid.geometry.coordinates;
        }

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: center,
          },
          properties: {
            ...feature.properties,
          },
        };
      });
      terra_store.map.getSource('barcode-scan-symbol-source').setData({
        type: 'FeatureCollection',
        features: symbol_collection,
      });
    }
    catch (error) {
      logger.error('Error loading barcode scan symbols:', error);
    }
  }

  return {
    STATUS_COLORS,
    STATUS_LABELS,
    getFeatureStyle,
    loadBarcodeScanImages,
    addBarcodeScanSymbols,
  };
}

export async function createSerialNumberImage(filename) {
  await load_js_css_file(
    'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js',
    'html2canvas-js',
    'js',
  );

  logger.log('html2canvas loaded', html2canvas);
  const feature_name_div = document.getElementById('feature_name_title');
  const serial_numbers_container = document.getElementById('serial_numbers_container');
  const feature_last_scanned_date = document.getElementById('feature_last_scanned_date');

  // Clone elements
  const feature_name_clone = feature_name_div.cloneNode(true);
  const serial_numbers_clone = serial_numbers_container.cloneNode(true);
  serial_numbers_clone.classList.add('-m-4', 'w-auto', 'max-w-none', 'overflow-visible');
  const feature_last_scanned_date_clone = feature_last_scanned_date.cloneNode(true);
  feature_last_scanned_date_clone.style.marginLeft = '-16px';

  // Create a temporary container
  const temp_container = document.createElement('div');
  temp_container.classList.add('absolute', '-top-[9999px]', '-left-[9999px]', 'bg-white', 'p-4', 'border', 'border-gray-300', 'w-max', 'overflow-visible');

  // Append clones to the container
  temp_container.appendChild(feature_name_clone);
  temp_container.appendChild(document.createElement('br'));
  temp_container.appendChild(document.createElement('hr'));
  temp_container.appendChild(document.createElement('br'));
  temp_container.appendChild(serial_numbers_clone);
  temp_container.appendChild(document.createElement('br'));
  temp_container.appendChild(feature_last_scanned_date_clone);

  document.body.appendChild(temp_container);

  // Calculate dimensions
  const total_height = temp_container.scrollHeight;
  const total_width = temp_container.scrollWidth;

  html2canvas(temp_container, {
    scrollX: 0,
    scrollY: 0,
    windowWidth: total_width,
    windowHeight: total_height,
    scale: 2, // Increase quality by scaling
  }).then((canvas) => {
    const dataUrl = canvas.toDataURL('image/jpg');
    if (dataUrl.length > 1000) { // Ensure we have a valid data URL
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${filename}.jpg`;
      link.click();
    }
    else {
      logger.error('Generated image data URL is empty or too small.');
    }
  }).catch((error) => {
    logger.error('Error capturing table:', error);
  }).finally(() => {
    // Clean up
    document.body.removeChild(temp_container);
    removeLoadedFile('html2canvas-js');
  });
}

export function useBarcodeScanValidationHelper() {
  // Validates x:y (single) or x:y-x1:y1 (adjacent range) position format
  function validatePosition(position) {
    const single_pos_regex = /^\d+:\d+$/;
    const range_pos_regex = /^\d+:\d+-\d+:\d+$/;

    if (single_pos_regex.test(position))
      return true;

    if (range_pos_regex.test(position)) {
      const [start, end] = position.split('-');
      const [r1, c1] = start.split(':').map(Number);
      const [r2, c2] = end.split(':').map(Number);
      return (Math.abs(r1 - r2) + Math.abs(c1 - c2)) === 1;
    }

    return false;
  }

  // Parses and validates indicator content string: {text,backgroundColor}
  function parseIndicatorContent(content) {
    if (!content.startsWith('{') || !content.endsWith('}'))
      return { is_valid: false, text: '', backgroundColor: '' };

    const inner = content.slice(1, -1);
    const comma_index = inner.indexOf(',');

    if (comma_index === -1)
      return { is_valid: false, text: '', backgroundColor: '' };

    const text = inner.slice(0, comma_index).trim();
    const backgroundColor = inner.slice(comma_index + 1).trim();

    if (!text || !backgroundColor)
      return { is_valid: false, text: '', backgroundColor: '' };

    return { is_valid: true, text, backgroundColor };
  }

  // Checks if a single row:col coordinate is within the feature's module grid bounds
  function isModulePositionValid(row, col, feature) {
    const num_modules_horizontal = Number.parseInt(feature?.properties?.extraProperties?.num_modules_horizontal);
    const num_modules_vertical = Number.parseInt(feature?.properties?.extraProperties?.num_modules_vertical);
    if (Number.isNaN(num_modules_horizontal) || Number.isNaN(num_modules_vertical))
      return false;

    // Get row_start and col_start offsets (default to 1 if not set or 0)
    let row_start = Number.parseInt(feature?.properties?.extraProperties?.row_start ?? 1);
    let col_start = Number.parseInt(feature?.properties?.extraProperties?.col_start ?? 1);

    // If row_start or col_start is 0, treat it as 1 (no offset)
    if (row_start === 0)
      row_start = 1;
    if (col_start === 0)
      col_start = 1;

    // Calculate the valid range based on offsets
    const row_end = row_start + num_modules_vertical - 1;
    const col_end = col_start + num_modules_horizontal - 1;

    return row >= row_start && col >= col_start && row <= row_end && col <= col_end;
  }

  // Parses indicator_config string into { position: { text, backgroundColor } } object
  function parseIndicatorConfig(config_string, feature_uid = null) {
    if (!config_string)
      return { is_valid: true, value: {} };

    const terra_store = feature_uid ? useTerraStore() : null;
    const feature = feature_uid ? terra_store.features_hash[feature_uid] : null;

    const value = {};
    let remaining = config_string.trim();

    while (remaining.length > 0) {
      const brace_index = remaining.indexOf('{');
      if (brace_index === -1)
        return { is_valid: false, value: {}, error_message: 'Invalid indicator_config format' };

      const position = remaining.slice(0, brace_index).replace(/,$/, '').trim();
      const closing_brace = remaining.indexOf('}', brace_index);

      if (closing_brace === -1)
        return { is_valid: false, value: {}, error_message: 'Invalid indicator_config format: unclosed brace' };

      const content = remaining.slice(brace_index, closing_brace + 1);

      if (!validatePosition(position))
        return { is_valid: false, value: {}, error_message: `Invalid position format: ${position}` };

      if (feature) {
        // Validate every coordinate in the position against the feature's module grid
        const coords = position.includes('-')
          ? position.split('-').map(p => p.split(':').map(Number))
          : [position.split(':').map(Number)];
        const has_invalid = coords.some(([row, col]) => !isModulePositionValid(row, col, feature));
        if (has_invalid)
          return { is_valid: false, value: {}, error_message: `Module position out of bounds: ${position}` };
      }

      const { is_valid, text, backgroundColor } = parseIndicatorContent(content);
      if (!is_valid)
        return { is_valid: false, value: {}, error_message: `Invalid indicator content format: ${content}` };

      value[position] = { text, backgroundColor };
      remaining = remaining.slice(closing_brace + 1).replace(/^,/, '').trim();
    }

    return { is_valid: true, value };
  }

  // Serializes indicator config object back into a config string
  function serializeIndicatorConfig(config_object) {
    if (!config_object || typeof config_object !== 'object')
      return '';
    return Object.entries(config_object)
      .map(([position, { text, backgroundColor }]) => `${position},{${text},${backgroundColor}}`)
      .join(',');
  }

  function validateModules(module_type, module_value, feature_uid) {
    const terra_store = useTerraStore();
    const feature = terra_store.features_hash[feature_uid];

    if (!module_value)
      return { is_valid: true, value: null, error_message: '' };

    let { is_valid, value } = terra_store.get_modules_details(module_value, 'save');
    let error_message = `Enter valid format for ${module_type}`;

    if (is_valid) {
      const has_invalid_modules = value.some((item) => {
        const [row, column] = item.split(':').map(Number);
        return Number.isNaN(row) || Number.isNaN(column) || !isModulePositionValid(row, column, feature);
      });
      if (has_invalid_modules) {
        is_valid = false;
        error_message = `Enter valid module numbers for ${module_type}`;
      }
    }
    if (is_valid && module_type === 'disabled_modules') {
      const has_serial_number = value.some((item) => {
        const serial_numbers = feature.properties.extraProperties?._serial_numbers || {};
        const [row, column] = item.split(':').map(Number);
        const serial_number = serial_numbers[`${row - 1}:${column - 1}`];
        if (serial_number) {
          error_message = `Remove serial number for module ${row}:${column} to disable it`;
        }
        return !!serial_number;
      });
      if (has_serial_number) {
        is_valid = false;
      }
    }

    return { is_valid, value, error_message };
  }

  return { validatePosition, parseIndicatorContent, parseIndicatorConfig, serializeIndicatorConfig, validateModules };
}
